import { reportError } from "@/utils/reportError";
import { trackObjectiveSet } from '@/utils/analytics';
import { supabase } from '@/utils/supabase';
import { useCallback, useEffect, useState } from 'react';

export interface UserObjectives {
  id: string;
  userId: string;
  dailyTarget: number;
  medalTier: 'bronze' | 'silver' | 'gold';
  selectedActionIds: number[];
  createdAt: string;
  updatedAt: string;
}

export function useUserObjectives() {
  const [objectives, setObjectives] = useState<UserObjectives | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchObjectives = useCallback(async () => {
    try {
      setLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) {
        setLoading(false);
        return;
      }

      const { data, error: fetchError } = await supabase
        .from('user_objectives')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') {
        // PGRST116 = no rows found - that's OK
        throw fetchError;
      }

      if (data) {
        setObjectives({
          id: data.id,
          userId: data.user_id,
          dailyTarget: data.daily_target,
          medalTier: data.medal_tier,
          selectedActionIds: data.selected_action_ids || [],
          createdAt: data.created_at,
          updatedAt: data.updated_at,
        });
      } else {
        setObjectives(null);
      }
    } catch (err: any) {
      reportError(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchObjectives();
  }, [fetchObjectives]);

  const updateObjectives = async (
    dailyTarget: number,
    selectedActionIds: number[]
  ): Promise<boolean> => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) return false;

      // Calculate medal tier based on daily target
      let medalTier: 'bronze' | 'silver' | 'gold' = 'bronze';
      if (dailyTarget >= 7) {
        medalTier = 'gold';
      } else if (dailyTarget >= 4) {
        medalTier = 'silver';
      }

      const { error: updateError } = await supabase
        .from('user_objectives')
        .upsert({
          user_id: user.id,
          daily_target: dailyTarget,
          medal_tier: medalTier,
          selected_action_ids: selectedActionIds,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: 'user_id'
        });

      if (updateError) throw updateError;

      trackObjectiveSet(dailyTarget, selectedActionIds.length);

      setObjectives(prev => prev ? {
        ...prev,
        dailyTarget,
        medalTier,
        selectedActionIds,
        updatedAt: new Date().toISOString(),
      } : null);

      return true;
    } catch (err: any) {
      reportError(err);
      setError(err.message);
      return false;
    }
  };

  const hasSetObjectives = objectives !== null && objectives.selectedActionIds.length > 0;

  return {
    objectives,
    loading,
    error,
    updateObjectives,
    hasSetObjectives,
    refetch: fetchObjectives,
  };
}
